/**
 * Storeit — Produktseite (Galerie)
 *
 * Kleines Custom Element für die Bildergalerie der Produktseite:
 * ein Klick auf ein Thumbnail blendet das zugehörige Hauptbild ein.
 *
 * Bewusst minimal gehalten:
 *   - kein DOMContentLoaded, das Element meldet sich selbst an
 *   - ein einziger delegierter Klick-Listener (sauber wieder abmeldbar)
 *   - ohne JavaScript bleibt das erste Bild sichtbar, die Seite funktioniert
 *
 * Die Variantenauswahl und der Warenkorb laufen NICHT über diese Datei,
 * sondern über die Horizon-Komponenten `variant-picker` und
 * `product-form-component`.
 */

import { StandardEvents } from '@shopify/events';

const ACTIVE_FRAME_CLASS = 'storeit-product-main__frame--active';
const ACTIVE_THUMB_CLASS = 'storeit-product-main__thumb--active';

class StoreitProductGallery extends HTMLElement {
  /** @type {HTMLElement[]} */
  #frames = [];

  /** @type {HTMLElement[]} */
  #thumbs = [];

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {number} */
  #index = 0;

  /** 21.10.: Wischen auf dem Bild (Mobil, statt Pfeilen) */
  #touchX = 0;
  #touchY = 0;

  #touchOnStage = false;

  #handleTouchStart = (/** @type {TouchEvent} */ event) => {
    const target = /** @type {Element} */ (event.target);
    this.#touchOnStage = !!target.closest?.('.storeit-product-main__stage');
    const t = event.touches[0];
    this.#touchX = t.clientX;
    this.#touchY = t.clientY;
  };

  #handleTouchEnd = (/** @type {TouchEvent} */ event) => {
    if (!this.#touchOnStage) return;
    const t = event.changedTouches[0];
    const dx = t.clientX - this.#touchX;
    const dy = t.clientY - this.#touchY;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    this.#step(dx < 0 ? 1 : -1);
  };

  connectedCallback() {
    this.#frames = Array.from(this.querySelectorAll('[data-storeit-gallery-frame]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-storeit-gallery-thumb]'));

    // Variantenwechsel: Galerie, Masse und Specs aus dem frisch gerenderten
    // Sektions-HTML uebernehmen (der Variant-Picker morpht nur sich selbst).
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // Bei nur einem Bild gibt es nichts zu schalten.
    if (this.#frames.length < 2 || this.#thumbs.length < 2) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    this.#select(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
    this.#frames = [];
    this.#thumbs = [];
  }

  /**
   * Uebernimmt nach einem Variantenwechsel Galerie, Massangabe und Specs
   * aus dem neu gerenderten HTML des Variant-Pickers.
   *
   * @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        const html = detail.html;

        for (const el of document.querySelectorAll('[data-storeit-swap]')) {
          const key = el.getAttribute('data-storeit-swap');
          const next = html.querySelector(`[data-storeit-swap="${key}"]`);
          if (next) el.replaceWith(next.cloneNode(true));
        }

        const newGallery = html.querySelector('storeit-product-gallery');
        if (newGallery && this.isConnected) {
          this.replaceWith(newGallery);
        }
      })
      .catch(() => {});
  };

  /**
   * Delegierter Klick-Handler für die Thumbnail-Reihe.
   *
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const prev = target.closest('[data-storeit-gallery-prev]');
    if (prev && this.contains(prev)) {
      this.#step(-1);
      return;
    }

    const next = target.closest('[data-storeit-gallery-next]');
    if (next && this.contains(next)) {
      this.#step(1);
      return;
    }

    const thumb = target.closest('[data-storeit-gallery-thumb]');

    if (!thumb || !this.contains(thumb)) return;

    const index = this.#thumbs.indexOf(/** @type {HTMLElement} */ (thumb));

    if (index === -1) return;

    this.#select(index);
  };

  /**
   * Pfeiltasten blättern die Galerie, sobald sie den Fokus hat.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.#step(event.key === 'ArrowLeft' ? -1 : 1);
  };

  /**
   * Blättert um `delta` Bilder weiter und läuft dabei um.
   *
   * @param {number} delta
   */
  #step(delta) {
    const total = this.#frames.length;

    if (total < 2) return;

    this.#select((this.#index + delta + total) % total);
  }

  /**
   * Blendet das Bild an Position `index` ein und markiert das Thumbnail.
   *
   * @param {number} index
   */
  #select(index) {
    this.#index = index;

    this.#frames.forEach((frame, i) => {
      const isActive = i === index;

      frame.classList.toggle(ACTIVE_FRAME_CLASS, isActive);
      frame.toggleAttribute('aria-hidden', !isActive);
    });

    this.#thumbs.forEach((thumb, i) => {
      const isActive = i === index;

      thumb.classList.toggle(ACTIVE_THUMB_CLASS, isActive);
      thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) {
        // Einzeilige Thumbnail-Reihe: aktives Bild waagerecht in den sichtbaren
        // Bereich holen (nur den Container scrollen, nie die Seite).
        const strip = thumb.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const left = thumb.offsetLeft;
          const right = left + thumb.offsetWidth;
          if (left < strip.scrollLeft) {
            strip.scrollTo({ left, behavior: 'smooth' });
          } else if (right > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({ left: right - strip.clientWidth, behavior: 'smooth' });
          }
        }
      }
    });
  }
}

if (!customElements.get('storeit-product-gallery')) {
  customElements.define('storeit-product-gallery', StoreitProductGallery);
}
